import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { BaseModalComponent } from '../base-modal/base-modal.component';
import { UserInfo } from '../../../dtos/user-info';
import { ProfileService } from '../../../services/profile.service';
import { ToastService } from '../../../services/toast.service';
import { ProfileUpdateRequest } from '../../../dtos/profile-update-request';

@Component({
  selector: 'app-profile-edit-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, BaseModalComponent],
  templateUrl: './profile-edit-modal.component.html',
  styleUrls: ['./profile-edit-modal.component.scss'],
})
export class ProfileEditModalComponent implements OnChanges {
  @Input() open = false;
  @Input() userInfo: UserInfo | null = null;
  @Output() openChange = new EventEmitter<boolean>();
  @Output() saved = new EventEmitter<UserInfo>();

  editForm: UntypedFormGroup;
  submitted = false;
  saving = false;
  errorMessage = '';

  constructor(
    private fb: UntypedFormBuilder,
    private profileService: ProfileService,
    private toastService: ToastService,
  ) {
    this.editForm = this.fb.group({
      firstName: ['', [Validators.required]],
      lastName: ['', [Validators.required]],
      email: ['', [Validators.required, Validators.email]],
      affiliation: [''],
      uiTheme: ['SYSTEM'],
      currentPassword: ['', [Validators.required]],
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['userInfo'] && this.userInfo) {
      this.editForm.patchValue({
        firstName: this.userInfo.firstName,
        lastName: this.userInfo.lastName,
        email: this.userInfo.email,
        affiliation: this.userInfo.affiliation || '',
        uiTheme: this.userInfo.uiTheme || 'SYSTEM',
        currentPassword: '',
      });
    }

    if (changes['open'] && this.open && this.userInfo) {
      this.submitted = false;
      this.errorMessage = '';
      this.editForm.patchValue({
        firstName: this.userInfo.firstName,
        lastName: this.userInfo.lastName,
        email: this.userInfo.email,
        affiliation: this.userInfo.affiliation || '',
        uiTheme: this.userInfo.uiTheme || 'SYSTEM',
      });
    }
  }

  close(): void {
    this.open = false;
    this.submitted = false;
    this.saving = false;
    this.errorMessage = '';
    this.openChange.emit(false);
  }

  save(): void {
    this.submitted = true;

    if (this.editForm.invalid || this.saving) {
      return;
    }

    this.saving = true;
    this.errorMessage = '';

    const values = this.editForm.value;
    const request: ProfileUpdateRequest = {
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
      email: values.email.trim(),
      affiliation: values.affiliation?.trim() || undefined,
      uiTheme: values.uiTheme || 'SYSTEM',
      currentPassword: values.currentPassword,
    };

    this.profileService.updateCurrentUser(request).subscribe({
      next: (updatedUser) => {
        this.toastService.success('Profile Updated', 'Your profile has been updated successfully');
        this.saved.emit(updatedUser);
        this.close();
      },
      error: (error) => {
        console.error('Could not update profile', error);
        this.errorMessage =
          typeof error?.error === 'string'
            ? error.error
            : error?.error?.message || 'Could not update your profile. Please try again.';
        this.toastService.error('Failed to Update Profile', this.errorMessage);
        this.saving = false;
      },
    });
  }
}
